<script setup>
// Internal counsel side panel: a private chat between the agent and the
// conversation's AI bot. Mounted once in the dashboard shell (like the copilot
// container) and decides its own visibility from the uiSettings flag, so no
// conversation view needs to know about it.
//
// Desktop width is user-resizable via a drag handle on the edge facing the
// main conversation; the chosen width is remembered per agent in localStorage.
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useEventListener } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { messageStamp } from 'shared/helpers/timeHelper';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import wootConstants from 'dashboard/constants/globals';
import {
  MODE_ASK,
  MODE_AGENT,
  DEFAULT_PANEL_WIDTH,
  loadPanelWidth,
  savePanelWidth,
  clampWidth,
} from '../preferences';

const { t } = useI18n();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();

const currentChat = useMapGetter('getSelectedChat');
const currentUser = useMapGetter('getCurrentUser');
const currentAccountId = useMapGetter('getCurrentAccountId');
const messageList = ref(null);
const draft = ref('');
const quotedDraft = ref(null);
const menuOpen = ref(false);
const menuRoot = ref(null);
// Lucide + explicit px: Phosphor *-bold masks are 256-viewBox at width:1em,
// so inherited font-size shrinks them to a speck (dots-three can vanish).
const ICON = 'inline-block !w-5 !h-5 shrink-0';

const conversationId = computed(() => currentChat.value?.id);
const userId = computed(() => currentUser.value?.id || null);
const accountId = computed(() => currentAccountId.value);
const isPanelOpen = computed(() => !!uiSettings.value.is_counsel_panel_open);
const shouldShow = computed(() => isPanelOpen.value && !!conversationId.value);

const isKbImage = message =>
  message?.metadata?.kind === 'kb_image' && !!message?.metadata?.asset_public_id;

const kbImageUrl = message => {
  // Prefer the signed blob path: an <img> cannot carry the dashboard API token.
  const signed = message?.metadata?.asset_url;
  if (signed) return signed;

  const id = message?.metadata?.asset_public_id;
  if (!id || !accountId.value) return '';
  return `/api/v1/accounts/${accountId.value}/article_assets/${id}`;
};

const messages = computed(() =>
  store.getters.counselMessagesFor(conversationId.value)
);
const flags = computed(() =>
  store.getters.counselUiFlagsFor(conversationId.value)
);
const botBound = computed(() =>
  store.getters.counselBotBoundFor(conversationId.value)
);
const mode = computed(() => store.getters.counselMode);
const canSend = computed(
  () =>
    !!draft.value.trim() &&
    !flags.value.sending &&
    !flags.value.awaitingReply &&
    botBound.value
);
// While the AI works the composer turns into a Stop control for the agent who
// started the turn. Everyone else only sees the thinking state.
const isThinking = computed(
  () => flags.value.awaitingReply || flags.value.sending
);
const canAbort = computed(() => {
  const actor = flags.value.actorUserId;
  if (actor == null || actor === '') return true;
  return Number(actor) === Number(userId.value);
});

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);
const isRTL = computed(() => document?.documentElement?.dir === 'rtl');

// --- resizable width (desktop only) -----------------------------------------
const panelWidth = ref(DEFAULT_PANEL_WIDTH);
const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

const desktopWidthStyle = computed(() => {
  if (isSmallScreen.value) return undefined;
  return { width: `${panelWidth.value}px`, minWidth: `${panelWidth.value}px` };
});

const restoreWidth = () => {
  panelWidth.value = loadPanelWidth(userId.value, windowWidth.value);
};

onMounted(restoreWidth);
watch(userId, restoreWidth);
watch(windowWidth, () => {
  // Keep the saved width inside the new viewport bounds when the window shrinks.
  panelWidth.value = clampWidth(panelWidth.value, windowWidth.value);
});

const getClientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

const onResizeStart = event => {
  if (isSmallScreen.value) return;
  isResizing.value = true;
  startX.value = getClientX(event);
  startWidth.value = panelWidth.value;
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
  event.preventDefault();
};

const onResizeMove = event => {
  if (!isResizing.value) return;
  if (event.touches) event.preventDefault();
  // Panel sits on the right in LTR: drag handle left → wider.
  // In RTL the panel sits on the left: drag handle right → wider.
  const delta = isRTL.value
    ? getClientX(event) - startX.value
    : startX.value - getClientX(event);
  panelWidth.value = clampWidth(startWidth.value + delta, windowWidth.value);
};

const onResizeEnd = () => {
  if (!isResizing.value) return;
  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });
  savePanelWidth(userId.value, panelWidth.value, windowWidth.value);
};

const onResizeHandleDoubleClick = () => {
  if (isSmallScreen.value) return;
  panelWidth.value = clampWidth(DEFAULT_PANEL_WIDTH, windowWidth.value);
  savePanelWidth(userId.value, panelWidth.value, windowWidth.value);
};

useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);

const scrollToBottom = async () => {
  await nextTick();
  const el = messageList.value;
  if (el) el.scrollTop = el.scrollHeight;
};

const loadThread = async () => {
  if (!shouldShow.value) return;
  store.dispatch('loadCounselMode');
  try {
    await store.dispatch('fetchCounselThread', conversationId.value);
    scrollToBottom();
  } catch (error) {
    useAlert(t('CUSTOM.COUNSEL.LOAD_FAILED'));
  }
};

watch([shouldShow, conversationId], loadThread, { immediate: true });
watch(() => messages.value.length, scrollToBottom);

const closePanel = () => {
  updateUISettings({ is_counsel_panel_open: false });
};

const closeOnSmallScreen = () => {
  if (isSmallScreen.value && isPanelOpen.value) closePanel();
};

const setMode = value => {
  store.dispatch('setCounselMode', value);
};

const send = async () => {
  const content = draft.value.trim();
  if (!content || flags.value.sending) return;

  const previous = draft.value;
  const previousQuote = quotedDraft.value;
  draft.value = '';
  quotedDraft.value = null;
  try {
    await store.dispatch('sendCounselMessage', {
      conversationId: conversationId.value,
      content,
      replyToMessageId: previousQuote?.id || null,
    });
    scrollToBottom();
  } catch (error) {
    // Give the text back so the agent does not lose what they typed.
    draft.value = previous;
    quotedDraft.value = previousQuote;
    const status = error?.response?.status;
    useAlert(
      t(
        status === 409
          ? 'CUSTOM.COUNSEL.TURN_IN_PROGRESS'
          : 'CUSTOM.COUNSEL.SEND_FAILED'
      )
    );
  }
};

const onEnter = event => {
  if (event.shiftKey) return;
  event.preventDefault();
  send();
};

const abort = () => {
  store.dispatch('abortCounselTurn', { conversationId: conversationId.value });
};

// Safety net: a turn that dies without writing anything (worker killed, host
// restarted) would otherwise leave the panel stuck on "thinking" forever. The
// longest legitimate turn is bounded by the host job (2 × 120s + retry delay).
const THINKING_TIMEOUT_MS = 300000;
let thinkingTimer = null;

watch(isThinking, value => {
  clearTimeout(thinkingTimer);
  if (!value || !canAbort.value) return;
  thinkingTimer = setTimeout(abort, THINKING_TIMEOUT_MS);
});

onUnmounted(() => clearTimeout(thinkingTimer));

const isHuman = message => message.role === 'human';
const isSystem = message => message.role === 'system';

// Bot replies (and the copy echoed back after send_to_customer) are markdown,
// so render them exactly like the main conversation bubbles instead of showing
// raw "**" / "-" syntax. Agent-typed text stays literal.
const formatMarkdown = content =>
  new MessageFormatter(content || '').formattedMessage;

const isCounselSent = message => message?.metadata?.kind === 'counsel_sent';

// Agents may prune human/assistant lines; system notices stay.
const isDeletable = message =>
  message?.role === 'human' || message?.role === 'assistant';

const deletingIds = ref(new Set());
const isDeleting = messageId => deletingIds.value.has(messageId);

const removeMessage = async message => {
  if (!isDeletable(message) || isDeleting(message.id)) return;
  if (!window.confirm(t('CUSTOM.COUNSEL.DELETE_CONFIRM'))) return;

  deletingIds.value = new Set([...deletingIds.value, message.id]);
  try {
    await store.dispatch('deleteCounselMessage', {
      conversationId: conversationId.value,
      messageId: message.id,
    });
  } catch (error) {
    useAlert(t('CUSTOM.COUNSEL.DELETE_FAILED'));
    // Re-sync so an optimistic remove that failed is restored.
    try {
      await store.dispatch('fetchCounselThread', conversationId.value);
    } catch (_reloadError) {
      /* alert already shown */
    }
  } finally {
    const next = new Set(deletingIds.value);
    next.delete(message.id);
    deletingIds.value = next;
  }
};

// Only the tail failure can be replayed; anything after it means the thread has
// moved on. The backend enforces the same rule.
const isRetryable = message =>
  message.metadata?.kind === 'turn_failed' &&
  message.metadata?.retryable === true &&
  message.id === messages.value[messages.value.length - 1]?.id;

const retry = async () => {
  if (flags.value.retrying || isThinking.value) return;
  try {
    await store.dispatch('retryCounselTurn', {
      conversationId: conversationId.value,
    });
    scrollToBottom();
  } catch (error) {
    useAlert(t('CUSTOM.COUNSEL.RETRY_FAILED'));
  }
};

const quotedMeta = message => message?.metadata?.reply_to || null;

const quoteSourceLabel = message => {
  if (isHuman(message)) {
    return message.sender_name || t('CUSTOM.COUNSEL.AGENT_FALLBACK');
  }
  if (isSystem(message)) return t('CUSTOM.COUNSEL.QUOTE_SYSTEM');
  return t('CUSTOM.COUNSEL.QUOTE_AI');
};

const quotedLabel = message => {
  const quote = quotedMeta(message);
  if (!quote) return '';
  if (quote.role === 'human') {
    return quote.sender_name || t('CUSTOM.COUNSEL.AGENT_FALLBACK');
  }
  if (quote.role === 'system') return t('CUSTOM.COUNSEL.QUOTE_SYSTEM');
  return t('CUSTOM.COUNSEL.QUOTE_AI');
};

const truncateText = (text, max = 80) => {
  const value = (text || '').replace(/\s+/g, ' ').trim();
  if (value.length <= max) return value;
  return `${value.slice(0, max)}…`;
};

const messageCopyText = message => {
  const text = (message?.content || '').trim();
  if (text) return text;
  if (isKbImage(message)) return t('CUSTOM.COUNSEL.KB_IMAGE_ALT');
  return '';
};

const copyMessage = async message => {
  const text = messageCopyText(message);
  if (!text) {
    useAlert(t('CUSTOM.COUNSEL.COPY_EMPTY'));
    return;
  }
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const input = document.createElement('textarea');
      input.value = text;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      input.remove();
    }
    useAlert(t('CUSTOM.COUNSEL.COPIED'));
  } catch (error) {
    useAlert(t('CUSTOM.COUNSEL.COPY_FAILED'));
  }
};

const quoteMessage = message => {
  quotedDraft.value = {
    id: message.id,
    label: quoteSourceLabel(message),
    preview: truncateText(messageCopyText(message) || t('CUSTOM.COUNSEL.KB_IMAGE_ALT')),
  };
};

const cancelQuote = () => {
  quotedDraft.value = null;
};

const toggleMenu = () => {
  menuOpen.value = !menuOpen.value;
};

const closeMenu = () => {
  menuOpen.value = false;
};

const clearChat = async () => {
  closeMenu();
  if (isThinking.value) {
    useAlert(t('CUSTOM.COUNSEL.CLEAR_BUSY'));
    return;
  }
  if (!window.confirm(t('CUSTOM.COUNSEL.CLEAR_CONFIRM'))) return;
  try {
    await store.dispatch('clearCounselThread', {
      conversationId: conversationId.value,
    });
    quotedDraft.value = null;
  } catch (error) {
    const status = error?.response?.status;
    useAlert(
      t(
        status === 409
          ? 'CUSTOM.COUNSEL.CLEAR_BUSY'
          : 'CUSTOM.COUNSEL.CLEAR_FAILED'
      )
    );
  }
};

useEventListener(document, 'click', event => {
  if (!menuOpen.value) return;
  const root = menuRoot.value;
  if (root && root.contains(event.target)) return;
  closeMenu();
});
</script>

<template>
  <div
    v-if="shouldShow"
    v-on-click-outside="() => closeOnSmallScreen()"
    class="bg-n-surface-2 h-full overflow-hidden flex flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full max-w-sm transition-[width] duration-150 ease-out md:relative md:max-w-none ltr:border-l rtl:border-r border-n-weak shadow-lg md:shadow-none"
    :class="{ 'transition-none': isResizing }"
    :style="desktopWidthStyle"
  >
    <!-- Drag handle between main conversation and counsel (desktop only). -->
    <div
      v-if="!isSmallScreen"
      class="hidden md:block absolute top-0 h-full w-1.5 cursor-col-resize z-50 ltr:left-0 rtl:right-0 group"
      :title="t('CUSTOM.COUNSEL.RESIZE_HINT')"
      @mousedown="onResizeStart"
      @touchstart="onResizeStart"
      @dblclick="onResizeHandleDoubleClick"
    >
      <div
        class="absolute inset-y-0 ltr:left-0 rtl:right-0 w-px bg-transparent group-hover:bg-n-brand transition-colors"
        :class="{ 'bg-n-brand': isResizing }"
      />
    </div>

    <div
      class="relative z-20 flex items-center justify-between flex-shrink-0 h-12 px-3 border-b border-n-weak"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('CUSTOM.COUNSEL.TITLE') }}
      </span>
      <div ref="menuRoot" class="relative flex items-center gap-0.5">
        <button
          type="button"
          class="flex items-center justify-center w-8 h-8 rounded-md text-n-slate-12 hover:bg-n-alpha-2"
          :title="t('CUSTOM.COUNSEL.MORE')"
          :aria-label="t('CUSTOM.COUNSEL.MORE')"
          @click.stop="toggleMenu"
        >
          <span :class="['i-lucide-ellipsis', ICON]" />
        </button>
        <div
          v-if="menuOpen"
          class="absolute top-full z-50 mt-1 ltr:right-8 rtl:left-8 min-w-[10rem] py-1 rounded-md border border-n-weak bg-n-solid-1 shadow-lg"
          @click.stop
        >
          <button
            type="button"
            class="w-full px-3 py-1.5 text-left text-sm text-n-ruby-11 hover:bg-n-alpha-2 disabled:opacity-40 disabled:cursor-not-allowed"
            :disabled="isThinking || flags.clearing"
            @click="clearChat"
          >
            {{ t('CUSTOM.COUNSEL.CLEAR') }}
          </button>
        </div>
        <button
          type="button"
          class="flex items-center justify-center w-8 h-8 rounded-md text-n-slate-12 hover:bg-n-alpha-2"
          :title="t('CUSTOM.COUNSEL.CLOSE')"
          :aria-label="t('CUSTOM.COUNSEL.CLOSE')"
          @click="closePanel"
        >
          <span :class="['i-lucide-x', ICON]" />
        </button>
      </div>
    </div>

    <div
      v-if="!botBound"
      class="flex-shrink-0 px-3 py-2 text-xs border-b text-n-slate-11 bg-n-alpha-1 border-n-weak"
    >
      {{ t('CUSTOM.COUNSEL.NO_BOT') }}
    </div>

    <div
      ref="messageList"
      class="flex flex-col flex-1 gap-3 p-3 overflow-y-auto"
    >
      <p
        v-if="!messages.length"
        class="px-2 py-6 text-xs text-center text-n-slate-10"
      >
        {{ t('CUSTOM.COUNSEL.EMPTY') }}
      </p>

      <template v-for="message in messages" :key="message.id">
        <div v-if="isSystem(message)" class="group relative px-2">
          <!-- The customer-visible copy is markdown; give it a readable block. -->
          <div
            v-if="isCounselSent(message)"
            v-dompurify-html="formatMarkdown(message.content)"
            class="px-3 py-2 border border-dashed rounded-lg prose prose-bubble max-w-none border-n-weak bg-n-alpha-1"
          />
          <div v-else class="text-xs text-center text-n-slate-10">
            {{ message.content }}
            <button
              v-if="isRetryable(message)"
              type="button"
              class="ltr:ml-1 rtl:mr-1 underline text-n-brand disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="flags.retrying || isThinking"
              @click="retry"
            >
              {{
                flags.retrying
                  ? t('CUSTOM.COUNSEL.RETRYING')
                  : t('CUSTOM.COUNSEL.RETRY')
              }}
            </button>
          </div>
          <div
            class="absolute -top-2 ltr:right-0 rtl:left-0 flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100"
          >
            <button
              type="button"
              class="flex items-center justify-center w-8 h-8 rounded-full border border-n-weak bg-n-solid-1 text-n-slate-12 shadow-sm hover:bg-n-alpha-2"
              :title="t('CUSTOM.COUNSEL.COPY')"
              @click="copyMessage(message)"
            >
              <span :class="['i-lucide-copy', ICON]" />
            </button>
            <button
              type="button"
              class="flex items-center justify-center w-8 h-8 rounded-full border border-n-weak bg-n-solid-1 text-n-slate-12 shadow-sm hover:bg-n-alpha-2"
              :title="t('CUSTOM.COUNSEL.QUOTE')"
              @click="quoteMessage(message)"
            >
              <span :class="['i-lucide-quote', ICON]" />
            </button>
          </div>
        </div>
        <div
          v-else
          class="group flex flex-col max-w-[85%] gap-1"
          :class="isHuman(message) ? 'items-end self-end' : 'items-start'"
        >
          <div class="relative">
            <div
              v-if="quotedMeta(message)"
              class="mb-1 px-2 py-1 text-[11px] rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-11"
            >
              <div class="font-medium truncate">{{ quotedLabel(message) }}</div>
              <div class="line-clamp-2 whitespace-pre-wrap">
                {{ truncateText(quotedMeta(message).content) }}
              </div>
            </div>
            <div
              class="px-3 py-2 text-sm break-words rounded-lg"
              :class="
                isHuman(message)
                  ? 'bg-n-brand text-white rounded-br-sm whitespace-pre-wrap'
                  : 'bg-n-alpha-1 text-n-slate-12 rounded-bl-sm'
              "
            >
              <a
                v-if="isKbImage(message)"
                :href="kbImageUrl(message)"
                target="_blank"
                rel="noopener noreferrer"
                class="block mb-2"
                :title="t('CUSTOM.COUNSEL.KB_IMAGE_ALT')"
              >
                <img
                  :src="kbImageUrl(message)"
                  :alt="message.content || t('CUSTOM.COUNSEL.KB_IMAGE_ALT')"
                  class="w-full max-h-56 rounded-md object-cover object-top bg-n-alpha-2"
                  loading="lazy"
                />
              </a>
              <span v-if="message.content && isHuman(message)">
                {{ message.content }}
              </span>
              <span
                v-else-if="message.content"
                v-dompurify-html="formatMarkdown(message.content)"
                class="prose prose-bubble max-w-none"
              />
            </div>
            <div
              class="absolute -top-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition flex items-center gap-1"
              :class="isHuman(message) ? 'ltr:-left-3 rtl:-right-3' : 'ltr:-right-3 rtl:-left-3'"
            >
              <button
                type="button"
                class="flex items-center justify-center w-8 h-8 rounded-full border border-n-weak bg-n-solid-1 text-n-slate-12 shadow-md hover:bg-n-alpha-2"
                :title="t('CUSTOM.COUNSEL.COPY')"
                @click="copyMessage(message)"
              >
                <span :class="['i-lucide-copy', ICON]" />
              </button>
              <button
                type="button"
                class="flex items-center justify-center w-8 h-8 rounded-full border border-n-weak bg-n-solid-1 text-n-slate-12 shadow-md hover:bg-n-alpha-2"
                :title="t('CUSTOM.COUNSEL.QUOTE')"
                @click="quoteMessage(message)"
              >
                <span :class="['i-lucide-quote', ICON]" />
              </button>
              <button
                v-if="isDeletable(message)"
                type="button"
                class="flex items-center justify-center w-8 h-8 rounded-full border border-n-ruby-9 bg-n-solid-1 text-n-ruby-11 shadow-md hover:bg-n-ruby-9 hover:text-white disabled:opacity-40"
                :title="t('CUSTOM.COUNSEL.DELETE')"
                :aria-label="t('CUSTOM.COUNSEL.DELETE')"
                :disabled="isDeleting(message.id)"
                @click="removeMessage(message)"
              >
                <span :class="['i-lucide-trash-2', ICON]" />
              </button>
            </div>
          </div>
          <div class="flex items-center gap-1 text-[10px] text-n-slate-10">
            <span v-if="isHuman(message)">
              {{ message.sender_name || t('CUSTOM.COUNSEL.AGENT_FALLBACK') }}
            </span>
            <span v-if="isHuman(message) && message.mode" class="uppercase">
              {{ message.mode }}
            </span>
            <span>{{ messageStamp(message.created_at) }}</span>
          </div>
        </div>
      </template>

      <div
        v-if="isThinking"
        class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-brand/10 border border-n-brand/30"
      >
        <span
          class="inline-block w-3.5 h-3.5 rounded-full border-2 border-n-brand border-t-transparent animate-spin"
        />
        <span class="text-xs font-medium text-n-brand animate-pulse">
          {{ t('CUSTOM.COUNSEL.WAITING') }}
        </span>
      </div>
    </div>

    <div class="flex-shrink-0 p-3 border-t border-n-weak">
      <!-- Initiator: Stop. Observer: status only, composer stays locked. -->
      <div
        v-if="isThinking && canAbort"
        class="flex items-center justify-center py-1"
      >
        <button
          type="button"
          class="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-semibold text-white rounded-lg shadow-sm cursor-pointer bg-n-ruby-9 hover:bg-n-ruby-10"
          @click="abort"
        >
          <span :class="['i-lucide-circle-stop', ICON]" />
          {{ t('CUSTOM.COUNSEL.ABORT') }}
        </button>
      </div>
      <div
        v-else-if="isThinking"
        class="px-1 py-2 text-xs text-center text-n-slate-11"
      >
        {{ t('CUSTOM.COUNSEL.WAITING_LOCKED') }}
      </div>
      <template v-else>
        <div
          v-if="quotedDraft"
          class="flex items-start gap-2 mb-2 px-2 py-1.5 text-xs rounded-md border border-n-weak bg-n-alpha-1"
        >
          <div class="flex-1 min-w-0">
            <div class="font-medium text-n-slate-12 truncate">
              {{ quotedDraft.label }}
            </div>
            <div class="text-n-slate-11 line-clamp-2">
              {{ quotedDraft.preview }}
            </div>
          </div>
          <button
            type="button"
            class="flex-shrink-0 text-n-slate-11 hover:text-n-slate-12"
            :title="t('CUSTOM.COUNSEL.QUOTE_CANCEL')"
            @click="cancelQuote"
          >
            <span :class="['i-lucide-x', ICON]" />
          </button>
        </div>
        <textarea
          v-model="draft"
          rows="3"
          class="w-full px-2 py-1.5 text-sm bg-n-alpha-1 border border-n-weak rounded-md resize-none text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
          :placeholder="t('CUSTOM.COUNSEL.PLACEHOLDER')"
          @keydown.enter="onEnter"
        />
        <div class="flex items-center justify-between mt-2">
          <div class="flex items-center p-0.5 rounded-md bg-n-alpha-1">
            <button
              v-for="value in [MODE_ASK, MODE_AGENT]"
              :key="value"
              type="button"
              class="px-2 py-1 text-xs rounded transition-colors"
              :class="
                mode === value
                  ? 'bg-n-brand text-white'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              :title="
                value === MODE_ASK
                  ? t('CUSTOM.COUNSEL.MODE_ASK_HINT')
                  : t('CUSTOM.COUNSEL.MODE_AGENT_HINT')
              "
              @click="setMode(value)"
            >
              {{
                value === MODE_ASK
                  ? t('CUSTOM.COUNSEL.MODE_ASK')
                  : t('CUSTOM.COUNSEL.MODE_AGENT')
              }}
            </button>
          </div>
          <button
            type="button"
            class="px-3 py-1 text-xs font-medium text-white rounded-md bg-n-brand disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="!canSend"
            @click="send"
          >
            {{ t('CUSTOM.COUNSEL.SEND') }}
          </button>
        </div>
      </template>
    </div>
  </div>
  <template v-else />
</template>
